import moment from "moment";

// Lógica pura de orden / filtro / formato de fecha de la tabla de documentos
// (ClassifyDocuments). Extraída del componente para poder testearla aislada y
// reutilizarla; no toca estado de Vue ni el store.

// Valor comparable de una fila para la columna activa. Centralizar el acceso
// hace que CUALQUIER columna sea ordenable con el mismo comparador, sin un
// branch por columna. Devuelve número (fechas/monto) o string en minúscula
// (texto y columnas derivadas de pills). `types` viene del componente (los
// tipos de atributo) para resolver la columna "attribute".
export function sortDocValue(entry, key, types = []) {
	switch (key) {
		case "emitted_at":
			return new Date(entry.emitted_at).getTime() || 0;
		case "accrual":
			// Mes Devengo: inicio del devengo si existe; si no, la fecha de
			// emisión (mismo dato que muestra la celda).
			return new Date(
				entry.has_accrual ? entry.init_accrual : entry.emitted_at
			).getTime() || 0;
		case "amount":
			return Number(entry.amount) || 0;
		case "doc_number":
			return entry.doc_number == null ? "" : String(entry.doc_number);
		case "doc_type":
			return (entry.doc_type || "").toLowerCase();
		case "rut":
			return (entry.rut || "").toLowerCase();
		case "description":
			return (entry.description || "").toLowerCase();
		case "alias":
			return (entry.alias || "").toLowerCase();
		case "mother_account": {
			const arr = entry.mother_accounts || [];
			return arr.length ? String(arr[0]).toLowerCase() : "";
		}
		case "accounts": {
			const arr = entry.accounts || [];
			return arr.length ? String(arr[0].level_3 || "").toLowerCase() : "";
		}
		case "attribute": {
			const firstType = types && types[0];
			const items = firstType && entry.attribute ? entry.attribute[firstType] : null;
			return items && items.length ? String(items[0].name || "").toLowerCase() : "";
		}
		default:
			return "";
	}
}

// Resta para números; localeCompare (es, numeric) para strings — así "Doc 2" <
// "Doc 10" y respeta acentos de forma estable.
export function compareSortValues(x, y) {
	if (typeof x === "number" && typeof y === "number") return x - y;
	return String(x).localeCompare(String(y), "es", { numeric: true, sensitivity: "base" });
}

// Ordena `docs` por la columna/dirección dadas. En estado "order" (3er click /
// default) respeta el orden original (sort estable). Desempates deterministas
// (independientes de la dirección): monto mayor, luego emisión más reciente,
// luego td_id. Muta y devuelve el array recibido (igual que Array.sort).
export function sortPartiallyDocs(docsToSort = [], sortColumn, sortDirection, types = []) {
	if (sortDirection !== "asc" && sortDirection !== "desc") {
		return docsToSort;
	}
	const dir = sortDirection === "asc" ? 1 : -1;
	return docsToSort.sort((a, b) => {
		const primary = compareSortValues(
			sortDocValue(a, sortColumn, types), sortDocValue(b, sortColumn, types)
		) * dir;
		if (primary !== 0) return primary;
		const byAmount = (Number(b.amount) || 0) - (Number(a.amount) || 0);
		if (byAmount !== 0) return byAmount;
		const byDate = (new Date(b.emitted_at).getTime() || 0) - (new Date(a.emitted_at).getTime() || 0);
		if (byDate !== 0) return byDate;
		return (a.td_id || 0) - (b.td_id || 0);
	});
}

// Predicado del filtro por pestaña (Ver Todos / Sin Clasificar / Contraparte /
// Documento).
export function matchesFilterTable(entry, filterTable) {
	switch (filterTable) {
		case "all":
			return true;
		case "Sin Clasificar":
			return entry.accounts.length == 0;
		case "Contraparte":
			return entry.is_classified_by_counterpart;
		case "Documento":
			return !entry.is_classified_by_counterpart && entry.accounts.length > 0;
		default:
			return false;
	}
}

// Búsqueda por número de documento (substring, case-insensitive) o monto exacto.
export function matchesSearch(entry, searchFilter) {
	const filter = (searchFilter || "").trim().toUpperCase();
	if (filter == "") {
		return true;
	}
	return entry.doc_number.toUpperCase().indexOf(filter) >= 0 ||
		entry.amount == filter;
}

// Normaliza un string de fecha al primer instante del día local (evita el
// corrimiento de zona horaria al parsear "YYYY-MM-DD").
export function initDateFirstDayMonthByStr(emittedDate) {
	const stuckTime = "T00:00:00";
	const date = new Date(emittedDate + stuckTime);
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function formatDocDate(emittedDate, format = "DD-MM-YYYY") {
	const date = initDateFirstDayMonthByStr(emittedDate);
	return moment(date).format(format);
}
